import React, { useEffect, useState, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import CartItems from './CartItems';
import Voucher from './Voucher';
import Totals from './Totals';
import KlarnaCheckout from './KlarnaCheckout';
import checkoutAnalytics from './checkoutAnalytics';
import Input from '../Elements/Input/Input';
import Button from '../Elements/Button/Button';
import Select from '../Elements/Select/Select';
import Radio from '../Elements/Radio/Radio';
import Checkbox from '../Elements/Checkbox/Checkbox';
import TemplateParser from '../Templates/TemplateParser';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import Overlay from '../../hocs/Overlay/Overlay';
import {
	initiatedScript,
	changeCountry,
	changePaymentMethod,
	changeShippingMethod,
	changeAddressInput,
	booleanChange,
	updateKlarna,
	updatePaymentMethod,
	purchase,
} from '../../actions/checkout';
import { goToPage, preloadPage } from '../../actions/environment';
import { purifier, addEvent, removeEvent, height, hasClass, fixBackgroundScroll } from '../../utils/dom';
import { isImageLoaded } from '../../utils/image';
import { translate } from '../../utils/locale';
import { camelCase, fieldValidation, upperCaseFirst } from '../../utils/string';
import {
	paymentMethodsMapping,
	shippingMethodsMapping,
	paymentMethodsOrder,
	addressFieldOrder,
} from '../../config';
import s from './Selection.scss';

const Selection = ({ checkout, setup, siteUpdated, pageData, ...props }) => {
	const { selection = {}, shippingAvailable, paymentUpdating = false } = checkout;
	const { paymentFields, paymentMethod, shippingMethods = [] } = selection;
	let { paymentMethods = [] } = selection;
	const states = setup.countries[setup.country].states ? Object.keys(setup.countries[setup.country].states).map((state) => ({ key: state, value: setup.countries[setup.country].states[state] })) : null;

	const summarySection = useRef(null);
	const policyLinks = useRef(null);
	const [windowHeight, updateWindowHeight] = useState();
	const [analytics, analyticsShown] = useState(false);
	const [counter, updateCounter] = useState(5);
	const [overlayShowing, toggleOverlay] = useState('');
	const [errors, updateErrors] = useState({
		address: {},
		shippingAddress: {},
	});

	useEffect(() => {
		const resizeHandler = () => {
			updateWindowHeight(window.innerHeight);
		};

		addEvent(window, 'resize.selectionFixed', () => {
			window.requestAnimationFrame(resizeHandler);
		});

		resizeHandler();

		return () => {
			removeEvent(window, 'resize.selectionFixed');
		};
	}, []);

	const [summarySectionHeight, updateSummarySectionHeight] = useState();
	useEffect(() => {
		let counterTimer = null;
		if (!selection?.items || (selection.items && selection.items.length === 0)) {
			counterTimer = window.setInterval(() => {
				updateCounter((prevState) => {
					if (prevState === 0) {
						props.goToPage('/');
						return 0;
					}
					return prevState - 1;
				});
			}, 1000);
		}

		let getRefTimeoutHandler;
		const getRef = () => {
			// Is main container visible.
			if (!summarySection.current) {
				getRefTimeoutHandler = setTimeout(getRef, 200);
				return;
			}
			updateSummarySectionHeight(height(summarySection.current));

			// Are all images loaded.
			const $imgs = summarySection.current.querySelectorAll('img');
			let allLoaded = true;
			[...$imgs].forEach(($img) => {
				if (!isImageLoaded($img)) allLoaded = false;
			});
			if (!allLoaded) {
				getRefTimeoutHandler = setTimeout(getRef, 200);
				return;
			}

			clearTimeout(getRefTimeoutHandler);
			updateSummarySectionHeight(height(summarySection.current));
		};

		getRef();

		if (policyLinks.current) {
			addEvent(policyLinks.current, 'click.linkCapture', (e) => {
				const $link = e.target.closest('a');

				if ($link && hasClass($link, 'linkCapture')) {
					e.preventDefault();
					e.stopPropagation();

					const loadPage = $link.getAttribute('href');
					props.preloadPage(loadPage);
					toggleOverlay(loadPage);
					fixBackgroundScroll(true);
				}
			});
		}

		return () => {
			window.clearInterval(counterTimer);
		};
	}, [selection]);

	const summarySectionPosition = windowHeight > summarySectionHeight + 100 ? '75px' : `calc(100vh - ${summarySectionHeight + 25}px)`;

	useEffect(() => {
		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		if (!analytics && checkout.selection.items && checkout.selection.items.length > 0) {
			checkoutAnalytics(checkout.selection.items, setup, checkout.selection.totals, checkout.listItemCache);
			analyticsShown(true);
			const hasStripePaymentIntentsLoaded = checkout.selection.paymentMethods.reduce((acc, method) => (method.paymentMethod === paymentMethodsMapping.stripePaymentIntents ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;
			if (Object.keys(hasStripePaymentIntentsLoaded).length > 0) props.purchase('selection', true, paymentMethodsMapping.stripePaymentIntents);
		}
	}, [checkout.selection]);

	let hasShipping = false;
	if (paymentFields) {
		Object.keys(paymentFields.shippingAddress).forEach((field) => {
			if (paymentFields.shippingAddress[field].visible) hasShipping = true;
		});
	}

	const closeOverlayHandler = () => {
		toggleOverlay('');
		fixBackgroundScroll(false);
	};

	const countries = Object.keys(setup.countries).filter((country) => setup.countries[country].shipTo).reduce((acc, country) => (country !== '0' ? [...acc, { key: country, value: setup.countries[country].name }] : [...acc]), []);
	const onCountryChange = (id, value) => {
		props.changeAddressInput('address', 'country', value);
		props.changeAddressInput('shippingAddress', 'country', value);
		props.changeCountry(value);
		props.updatePaymentMethod();
		props.updateKlarna();
	};
	const onShippingCountryChange = (id, value) => {
		props.changeAddressInput('shippingAddress', 'country', value);
		props.changeCountry(value);
		props.updatePaymentMethod();
		props.updateKlarna();
	};

	paymentMethods = paymentMethods.sort((a, b) => {
		const posA = paymentMethodsOrder.indexOf(a.paymentMethod) !== -1 ? paymentMethodsOrder.indexOf(a.paymentMethod) : 1000;
		const posB = paymentMethodsOrder.indexOf(b.paymentMethod) !== -1 ? paymentMethodsOrder.indexOf(b.paymentMethod) : 1000;
		return posA - posB;
	});

	const availablePaymentMethods = paymentMethods
		.filter((method) => method.paymentMethod !== paymentMethodsMapping.stripePaymentIntents)
		.reduce((acc, method) => [...acc, { key: method.paymentMethod, value: (setup.statics[camelCase(method.name)] ? translate(setup, camelCase(method.name)) : method.name) }], []) || [];
	const hasKlarna = paymentMethods.filter((method) => method.paymentMethod === paymentMethodsMapping.klarnaCheckout) || false;
	const hasAdyenDropIn = paymentMethods.reduce((acc, method) => (method.paymentMethod === paymentMethodsMapping.adyenDropIn ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;
	const hasStripeCheckout = paymentMethods.reduce((acc, method) => (method.paymentMethod === paymentMethodsMapping.stripeCheckout ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;
	const hasStripePaymentIntents = paymentMethods.reduce((acc, method) => (method.paymentMethod === paymentMethodsMapping.stripePaymentIntents ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;

	const onPaymentMethodChange = (value) => {
		props.changePaymentMethod(value);
	};

	const availableShippingMethods = shippingMethods.reduce((acc, method) => [...acc, { key: method.shippingMethod, value: (setup.statics[camelCase(method.name)] ? translate(setup, camelCase(method.name)) : method.name.replace(/^\d+/, '')), disabled: shippingAvailable[method.shippingMethod] === false }], []) || [];
	const hasBudbee = shippingMethods.reduce((acc, method) => (method.shippingMethod === shippingMethodsMapping.budbee ? { ...acc, [method.shippingMethod]: method } : acc), {}) || false;

	const onShippingMethodChange = (value) => {
		props.changeShippingMethod(value);
	};

	const getIdAndGroup = (id) => {
		if (id.indexOf('shippingAddress') !== -1) return ['shippingAddress', String(id).replace('shippingAddress-', '')];
		if (id.indexOf('address') !== -1) return ['address', String(id).replace('address-', '')];
		return [false, String(id)];
	};

	const onAddressInputChange = (id, value) => {
		const [group, field] = getIdAndGroup(id);
		props.changeAddressInput(group, field, value);
		updateErrors((prevState) => {
			if (group) {
				const { [field]: removed, ...nextState } = prevState[group];
				return { ...prevState, [group]: { ...nextState } };
			}
			const { [field]: removed, ...nextState } = prevState;
			return { ...nextState };
		});
	};

	const onAddressInputBlur = (id, value, all = false) => {
		const [group, field] = getIdAndGroup(id);

		onAddressInputChange(id, value);

		if (
			fieldValidation(value, field) // Is field invalid?
			&& ( // And is required
				!selection?.paymentFields?.[group]?.[field] || selection?.paymentFields?.[group]?.[field]?.required // Is in a group.
				|| !selection?.paymentFields?.[field] || selection?.paymentFields?.[field]?.required // Is not in a group.
				|| (field === 'state' && states) || field === 'country'
			)
			&& (group !== 'shippingAddress' || (group === 'shippingAddress' && selection.useShippingAddress)) // And is address or is using shipping address.
		) {
			updateErrors((prevState) => {
				if (group) {
					return {
						...prevState,
						[group]: {
							...prevState[group],
							[field]: translate(setup, `error${fieldValidation(value, field)}`),
						},
					};
				}
				return {
					...prevState,
					[field]: translate(setup, `error${fieldValidation(value, field)}`),
				};
			});
			return true;
		}

		if (!fieldValidation(value, field) && !all) {
			// Is valid update methods.
			props.updatePaymentMethod();
			props.updateKlarna();
		}
		return false;
	};

	const onBooleanChange = (id, value) => {
		props.booleanChange(id);
		onAddressInputBlur(id, value);
		if (paymentMethod === paymentMethodsMapping.klarnaCheckout && id === 'subscribeToNewsletter') props.updateKlarna();
		if (id === 'useShipping' && value === false) {
			props.changeAddressInput('address', 'country', checkout.payment.shippingAddress.country);
		}
		props.updatePaymentMethod();
	};

	const onPurchase = (e) => {
		e.preventDefault();

		// Ensure that we are using data from the fields as it may be autofilled.
		const $inputs = e.currentTarget.querySelectorAll('input,select');
		let hasErrors = false;

		[...$inputs].forEach(($input) => {
			const id = $input.getAttribute('id');
			let value = $input.value;
			if ($input.type === 'checkbox' && !$input.checked) value = 0;
			const returnErrors = onAddressInputBlur(id, value, true);
			if (returnErrors) hasErrors = true;
		});

		if (hasErrors || Object.keys(errors.address).length > 0 || Object.keys(errors.shippingAddress).length > 0 || Object.keys(errors).length > 2) return;

		props.updatePaymentMethod();
		props.updateKlarna();
		props.purchase();
	};

	return (
		<main role="main">
			{
				!selection.items || selection.items.length === 0 ? (
					<div className={s.selection}>
						{ siteUpdated && <h1 className={s.selection_title__empty}>{ translate(setup, 'checkoutNoItems') }</h1> }
						{ siteUpdated && <span>{ `${translate(setup, 'checkoutEmptyReturningHome')} ${counter}` }</span> }
					</div>
				) : (
					<div className={s.selection}>
						<h1 className={s.selection_title}>{ translate(setup, 'checkoutTitle') }</h1>
						<div className={[s.selection_summary].join(' ')} ref={summarySection} style={{ top: summarySectionPosition }}>
							<div className={s.selection_part}>
								<ErrorBoundary message="Product data missing" debugData={selection}>
									<h5 className={s.selection_part_title}>{ translate(setup, 'checkoutYourItemsTitle') }</h5>
									<div className={s.selection_part_cartOverflow}>
										<CartItems currentPage="selection" />
									</div>
									<div className={s.selection_part_totals}>
										<Voucher setup={setup} checkout={checkout} />
										<Totals setup={setup} selection={selection} />
									</div>
									{ setup.statics.checkoutCustomerService ? <div className={s.selection_part_customerService} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'checkoutCustomerService')) }} /> : null }
								</ErrorBoundary>
							</div>
						</div>
						{
							setup.unableToSendToCountry ? (
								<div className={s.selection_info}>
									<div className={s.selection_part}>
										<h5 className={s.selection_part_title}>{ translate(setup, 'checkoutDeliveryTitleChangeCountry') }</h5>
										<div className={s.selection_part_inner}>
											<div className={s.selection_part_country}>
												<Select id="country" selected={setup.country} options={countries} inputChange={onCountryChange} label={translate(setup, 'labelCountryShipping')} />
											</div>
										</div>
									</div>
								</div>
							) : (
								<div className={s.selection_info}>
									<div className={s.selection_part}>
										<h5 className={s.selection_part_title}>{ translate(setup, 'checkoutDeliveryTitle') }</h5>
										<div className={s.selection_part_inner}>
											<div className={[s.selection_part_country, (availableShippingMethods.length > 0 ? s.selection_part_country__shippingMethods : null)].join(' ')}>
												<Select id="country" selected={setup.country} options={countries} inputChange={onCountryChange} label={translate(setup, 'labelCountryShipping')} />
												{
													states ? (
														<div className={s.selection_part_address_row}>
															<Select id="address-state" selected={checkout.payment.address?.state} options={[{ key: '', value: translate(setup, 'chooseState') }, ...states]} label={translate(setup, 'labelState')} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={errors.address?.state?.error} />
														</div>
													) : null
												}
											</div>
											<ErrorBoundary message="Shipping methods" debugData={selection}>
												<div className={s.selection_part_shipping}>
													<Radio id="shipping" selected={selection.shippingMethod} options={availableShippingMethods} inputChange={onShippingMethodChange} label={translate(setup, 'checkoutDeliveredBy')} />
												</div>
											</ErrorBoundary>
										</div>
									</div>
									{
										Object.keys(hasStripePaymentIntents).length > 0 && (
											<div className={[s.selection_part, s.selection_stripe].join(' ')}>
												<div id="stripePaymentIntentsContainer" />
												<div className={s.selection_stripe_paymentButton} id="stripeCheckoutButton" />
												<h5 className={s.selection_part_title}><span>{ translate(setup, 'checkoutExpressTitle') }</span></h5>
											</div>
										)
									}
									<div className={s.selection_part}>
										<h5 className={s.selection_part_title}>{ translate(setup, 'checkoutPaymentTitle') }</h5>
										<div className={s.selection_part_inner}>
											<div className={s.selection_part_payment}>
												<ErrorBoundary message="Payment methods" debugData={selection}>
													<div className={s.selection_part_payment_choice}>
														<Radio id="payment" selected={selection.paymentMethod} options={availablePaymentMethods} inputChange={onPaymentMethodChange} label={translate(setup, 'checkoutProcessedBy')} />
													</div>
												</ErrorBoundary>
											</div>
											{ selection.paymentMethod === paymentMethodsMapping.klarnaCheckout && <Checkbox id="subscribeToNewsletter" label={translate(setup, 'subscribeToNewsletter')} selected={checkout.payment.address.newsletter} inputChange={onBooleanChange} /> }
										</div>
										{ selection.paymentMethod !== paymentMethodsMapping.klarnaCheckout && <h5 className={s.selection_part_title}>{ translate(setup, 'checkoutInformationTitle') }</h5> }
										{ selection.paymentMethod !== paymentMethodsMapping.klarnaCheckout && (
											<div className={s.selection_part_inner}>
												<div className={s.selection_part_address}>
													<ErrorBoundary message="Billing address" debugData={paymentFields}>
														{
															addressFieldOrder.map((field) => {
																if (!paymentFields.address[field].visible) return null;
																if (field === 'state') {
																	if (states) {
																		return (
																			<div key={`address-${field}`} className={s.selection_part_address_row}>
																				<Select id={`address-${field}`} selected={checkout.payment.address[field]} options={[{ key: '', value: translate(setup, 'chooseState') }, ...states]} label={translate(setup, `label${upperCaseFirst(field)}`)} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={errors.address?.[field]} />
																			</div>
																		);
																	}
																	return null;
																}
																let fieldType = 'text';
																if (field === 'email') fieldType = 'email';
																if (field === 'phoneNumber') fieldType = 'tel';
																const readOnlyZipCode = field === 'zipCode' && Object.keys(hasBudbee).length > 0 && ((hasShipping && !checkout.useShippingAddress) || !hasShipping) ? 1 : 0;
																const readOnlyField = readOnlyZipCode;
																const fieldEvents = readOnlyField ? {} : {
																	inputChange: onAddressInputChange,
																	inputBlur: onAddressInputBlur,
																	isError: errors.address?.[field],
																};

																return (
																	<div key={`address-${field}`} className={[s.selection_part_address_row, (readOnlyField ? s.selection_part_address_row__disabled : null)].join(' ')}>
																		<Input id={`address-${field}`} type={fieldType} readOnly={readOnlyField} value={checkout.payment.address[field]} label={translate(setup, `label${upperCaseFirst(field)}`)} {...fieldEvents} />
																	</div>
																);
															})
														}
														{
															hasShipping ? (
																<div className={s.selection_part_address_row}>
																	<Select id="address-country" selected={checkout.payment.address.country} options={countries} label={translate(setup, 'labelCountry')} inputChange={onCountryChange} />
																</div>
															) : null
														}
													</ErrorBoundary>
												</div>
												{
													hasShipping && (
														<div className={s.selection_part_shippingAddress}>
															<ErrorBoundary message="Shipping address" debugData={paymentFields}>
																<Checkbox id="useShipping" label={translate(setup, 'checkoutAlternateAddress')} selected={checkout.useShippingAddress} inputChange={onBooleanChange} />
																{
																	checkout.useShippingAddress && addressFieldOrder.map((field) => {
																		if (!paymentFields.address[field].visible) return null;
																		if (field === 'state') {
																			if (states) {
																				return (
																					<div key={`shippingAddress-${field}`} className={s.selection_part_address_row}>
																						<Select id={`shippingAddress-${field}`} selected={checkout.payment.shippingAddress[field]} options={[{ key: '', value: translate(setup, 'chooseState') }, ...states]} label={translate(setup, `label${upperCaseFirst(field)}`)} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={errors.shippingAddress?.[field]} />
																					</div>
																				);
																			}
																			return null;
																		}
																		let fieldType = 'text';
																		if (field === 'email') fieldType = 'email';
																		if (field === 'phoneNumber') fieldType = 'tel';
																		return (
																			<div key={`shippingAddress-${field}`} className={s.selection_part_address_row}>
																				<Input id={`shippingAddress-${field}`} type={fieldType} value={checkout.payment.shippingAddress[field]} label={translate(setup, `label${upperCaseFirst(field)}`)} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={errors.shippingAddress?.[field]} />
																			</div>
																		);
																	})
																}
																{
																	checkout.useShippingAddress && (
																		<div className={s.selection_part_address_row}>
																			<Select id="shippingAddress-country" selected={checkout.payment.shippingAddress.country} options={countries} label={translate(setup, 'labelCountry')} inputChange={onShippingCountryChange} />
																		</div>
																	)
																}
															</ErrorBoundary>
														</div>
													)
												}
												<Checkbox id="subscribeToNewsletter" label={translate(setup, 'subscribeToNewsletter')} selected={checkout.payment.address.newsletter} inputChange={onBooleanChange} />
												<Checkbox id="termsAndConditions" innerHTML={translate(setup, 'checkoutAcceptTerms')} selected={checkout.payment.termsAndConditions} inputChange={onBooleanChange} isError={errors.termsAndConditions} />
											</div>
										)}
										{
											hasKlarna.length > 0 && (
												<div className={[s.selection_part_purchase, s.selection_part_purchase__klarna].join(' ')} style={{ display: (selection.paymentMethod !== paymentMethodsMapping.klarnaCheckout ? 'none' : 'block') }}>
													<ErrorBoundary message="Klarna checkout" debugData={selection}>
														<KlarnaCheckout paymentMethod={selection.paymentMethod} />
													</ErrorBoundary>
												</div>
											)
										}
										{
											paymentFields.termsAndConditions.visible && (
												<div className={s.selection_part_purchase}>
													<ErrorBoundary message="Complete payment" debugData={checkout.payment}>
														{ selection.paymentMethod.indexOf('paypal') !== -1 && <div className={s.selection_part_purchase_error}>{ translate(setup, 'paypalMessage') }</div> }
														{ checkout.selectionError && <div className={s.selection_part_purchase_error}>{ translate(setup, 'checkoutGeneralError') }</div> }
														{ checkout.selectionErrorStock && <div className={s.selection_part_purchase_error}>{ translate(setup, 'checkoutStockError') }</div> }
														{ (!checkout.adyenInitiated || checkout.selectionError || checkout.selectionErrorStock) && <Button text={translate(setup, 'checkoutPayment')} type="submit" clickEventHandler={onPurchase} /> }
													</ErrorBoundary>
												</div>
											)
										}
										{
											Object.keys(hasAdyenDropIn).length > 0 && (
												<div className={[s.selection_paymentContainer, (paymentUpdating ? s.selection_paymentContainer__updating : null)].join(' ')} id="adyenDropInContainer" />
											)
										}
										{
											Object.keys(hasStripeCheckout).length > 0 && (
												<div className={[s.selection_paymentContainer, (paymentUpdating ? s.selection_paymentContainer__updating : null)].join(' ')} id="stripeCheckoutContainer" />
											)
										}
									</div>
								</div>
							)
						}
					</div>
				)
			}
			<Overlay variantClasses={['overlay__selection']} isShowing={overlayShowing} closeOverlayHandler={closeOverlayHandler}>
				{ pageData?.[overlayShowing] && <TemplateParser sections={pageData[overlayShowing]?.content?.sections || []} setup={setup} isOverlay /> }
			</Overlay>
		</main>
	);
};

const mapState = ({ checkout, environment }, { data }) => (
	{ checkout, setup: environment.setup, siteUpdated: environment.properties.siteUpdated, data }
);

export default hot(module)(connect(mapState, {
	initiatedScript,
	changeCountry,
	changePaymentMethod,
	changeShippingMethod,
	changeAddressInput,
	booleanChange,
	updateKlarna,
	updatePaymentMethod,
	purchase,
	goToPage,
	preloadPage,
})(Selection));
